import { extendZodWithOpenApi, z } from '@hono/zod-openapi'

// Split worker bundles can evaluate this schema chunk before Hono's initialization chunk.
// Make the dependency explicit; the extension is safe to apply more than once.
extendZodWithOpenApi(z)

/**
 * Standard error envelope returned by every v1 endpoint when something
 * goes wrong. `error` is a stable machine-readable code; `message` is a
 * human-readable explanation; `details` carries Zod issues for 400s.
 */
export const ErrorSchema = z
  .object({
    code: z.string().optional().openapi({
      description: 'Stable machine code for this failure. Match on it rather than on the message.',
    }),
    detail: z.string().optional().openapi({
      description: 'RFC 9457 human-readable explanation of this occurrence.',
    }),
    details: z.unknown().optional(),
    error: z.string(),
    eventId: z.string().uuid().optional(),
    instance: z.string().optional().openapi({
      description: 'RFC 9457 URI for this occurrence. Carries the request id.',
    }),
    isRetryable: z
      .boolean()
      .optional()
      .openapi({
        description:
          'Whether repeating the identical request is safe. When false the request must not be ' +
          'retried, even if the status looks transient.',
      }),
    message: z.string().optional(),
    provider: z.string().optional(),
    requestId: z.string().optional(),
    status: z
      .union([z.number().int(), z.string()])
      .optional()
      .openapi({
        description:
          'Usually the RFC 9457 copy of the HTTP status code. On send failures it is instead the ' +
          'outcome discriminator: `acceptance_unknown` means the provider may already have ' +
          'delivered the message, so it must not be sent again.',
      }),
    title: z.string().optional().openapi({
      description: 'RFC 9457 short human-readable summary of the problem type.',
    }),
    type: z.string().optional().openapi({
      description: 'RFC 9457 problem type URI identifying the error kind.',
    }),
  })
  .openapi('ApiError')

/** `[workspaceId]` segment shared by every workspace-scoped route. */
export const WorkspaceParamSchema = z
  .object({
    workspaceId: z.string().min(1).openapi({ example: 'haven-vacation-rentals' }),
  })
  .openapi('WorkspaceParam')

/** UUID path/query parameter used by entity-scoped routes. */
export const UuidSchema = z
  .string()
  .uuid()
  .openapi({ example: '11111111-1111-4111-8111-111111111111' })

/**
 * UUID matching Postgres semantics: any 8-4-4-4-12 hex string, regardless of
 * RFC version/variant nibbles. Zod's `.uuid()` is stricter than the `uuid`
 * column type and rejects seeded/imported rows (e.g.
 * `00000000-0000-0000-0000-000000000601`), which turns valid DB rows into
 * response-contract 500s. Use this for ids that round-trip from the database.
 */
export const PgUuidSchema = z
  .string()
  .regex(/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/)
  .openapi({ example: '11111111-1111-4111-8111-111111111111' })

/** ISO timestamp returned by the API. */
export const DateTimeStringSchema = z
  .string()
  .datetime()
  .openapi({ example: '2026-05-22T17:00:00.000Z', format: 'date-time' })

/** ISO calendar date accepted by date filters. */
export const DateStringSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .openapi({ example: '2026-05-22', format: 'date' })

/** Time-only field in 24-hour format. */
export const TimeStringSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/)
  .openapi({ example: '10:30:00' })

/** Common limit/offset pagination query. */
export const PaginationQuerySchema = z
  .object({
    limit: z.coerce
      .number()
      .int()
      .min(1)
      .max(100)
      .default(50)
      .openapi({ description: 'Maximum number of items to return.', example: 50 }),
    offset: z.coerce
      .number()
      .int()
      .min(0)
      .default(0)
      .openapi({ description: 'Number of items to skip.', example: 0 }),
  })
  .openapi('PaginationQuery')

/** Pagination metadata returned by list routes. */
export const PaginationMetaSchema = z
  .object({
    hasMore: z.boolean().openapi({ example: true }),
    limit: z.number().int().positive().openapi({ example: 50 }),
    offset: z.number().int().nonnegative().openapi({ example: 0 }),
    total: z.number().int().nonnegative().openapi({ example: 125 }),
  })
  .openapi('PaginationMeta')

/** The `meta` block of a resource response. */
export const ResponseMetaSchema = z
  .object({
    requestId: z.string().openapi({
      description: 'Identifies this request in logs and support. Matches the X-Request-Id header.',
      example: 'req_3f2c9a7e1b6d4f08a5c2e9d7b1a4c6e0',
    }),
  })
  .openapi('ResponseMeta')

/** Wraps a resource schema in the single-resource envelope `{ data, meta }`. */
export function dataEnvelope<T extends z.ZodType>(data: T) {
  return z.object({ data, meta: ResponseMetaSchema })
}

/** `{ data: { deleted }, meta }`, returned by routes that remove one item. */
export const DeletionResponseSchema = dataEnvelope(
  z.object({ deleted: z.boolean() }).openapi('DeletionResult')
)

/** The `links` block of a list response. */
export const PageLinksSchema = z
  .object({
    self: z.string().openapi({ description: 'This page, as a path with its query.' }),
    next: z.string().nullable().openapi({
      description: 'The next page, as a path with its query, or null on the last page.',
    }),
  })
  .openapi('PageLinks')

/** Links for a cursor-paged list: this page and, when there is one, the next. */
export function pageLinks(
  requestUrl: string,
  nextCursor: string | null
): z.infer<typeof PageLinksSchema> {
  const url = new URL(requestUrl)
  const self = `${url.pathname}${url.search}`
  if (!nextCursor) {
    return { self, next: null }
  }
  url.searchParams.set('cursor', nextCursor)
  return { self, next: `${url.pathname}${url.search}` }
}

/** Standard error responses every authenticated workspace route emits. */
export const standardErrorResponses = {
  400: {
    content: { 'application/problem+json': { schema: ErrorSchema } },
    description: 'Validation error',
  },
  401: {
    content: { 'application/problem+json': { schema: ErrorSchema } },
    description: 'Unauthorized',
  },
  403: {
    content: { 'application/problem+json': { schema: ErrorSchema } },
    description: 'Forbidden',
  },
  404: {
    content: { 'application/problem+json': { schema: ErrorSchema } },
    description: 'Not found',
  },
  409: {
    content: { 'application/problem+json': { schema: ErrorSchema } },
    description: 'Conflict',
  },
  422: {
    content: { 'application/problem+json': { schema: ErrorSchema } },
    description: 'Unprocessable entity',
  },
  500: {
    content: { 'application/problem+json': { schema: ErrorSchema } },
    description: 'Internal error',
  },
} as const

export function idList(value: string | string[] | undefined): string[] | undefined {
  if (value === undefined) {
    return undefined
  }
  return Array.isArray(value) ? value : [value]
}
